import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Chip, Typography, Paper, Autocomplete, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { supabase } from '../lib/supabase';
import MathRenderer from './MathRenderer';

function ProblemForm({ problem, onSave }) {
    const [content, setContent] = useState(problem.content || '');
    const [solution, setSolution] = useState(problem.solution || '');
    const [difficulty, setDifficulty] = useState(problem.difficulty || '');
    const [selectedTechniques, setSelectedTechniques] = useState(problem.techniques || []);
    const [selectedTopics, setSelectedTopics] = useState(problem.topics || []);
    const [selectedSources, setSelectedSources] = useState(problem.sources || []); // State for selected sources
    const [topics, setTopics] = useState([]);
    const [sources, setSources] = useState([]); // State for all available sources
    const [techniques, setTechniques] = useState([]); // State for all available techniques

    const difficultyOptions = ['easy', 'medium', 'hard', 'very hard'];

    useEffect(() => {
        async function fetchTopicsSourcesAndTechniques() {
            // Fetch topics
            const { data: topicsData, error: topicsError } = await supabase.from('curriculum_topics').select('*');
            if (topicsError) {
                console.error('Error fetching topics:', topicsError);
            } else {
                setTopics(topicsData || []);
            }

            // Fetch sources
            const { data: sourcesData, error: sourcesError } = await supabase.from('sources').select('*');
            if (sourcesError) {
                console.error('Error fetching sources:', sourcesError);
            } else {
                setSources(sourcesData || []);
            }

            // Fetch techniques
            const { data: techniquesData, error: techniquesError } = await supabase.from('techniques').select('*');
            if (techniquesError) {
                console.error('Error fetching techniques:', techniquesError);
            } else {
                setTechniques(techniquesData || []);
            }
        }
        fetchTopicsSourcesAndTechniques();
    }, []);

    // Update form fields when problem prop changes
    useEffect(() => {
        setContent(problem.content || '');
        setSolution(problem.solution || '');
        setDifficulty(problem.difficulty || '');
        setSelectedTechniques(problem.techniques || []);
        setSelectedTopics(problem.topics || []);
        setSelectedSources(problem.sources || []);
    }, [problem]);

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('ProblemForm - selectedSources:', selectedSources);
        console.log('ProblemForm - selectedTopics:', selectedTopics);
        console.log('ProblemForm - selectedTechniques:', selectedTechniques);
        console.log('ProblemForm - difficulty:', difficulty);

        console.log('ProblemForm - problem object being passed:', { ...problem, content, solution, difficulty, techniques: selectedTechniques, topics: selectedTopics, sources: selectedSources });
        onSave({ ...problem, content, solution, difficulty, techniques: selectedTechniques, topics: selectedTopics, sources: selectedSources });
    };

    const handleTopicChange = (event, newValue) => {
        const topicIds = newValue.map(topic => topic.id);
        setSelectedTopics(topicIds);
    };

    const handleSourceChange = (event, newValue) => {
        const sourceIds = newValue.map(source => source.id);
        setSelectedSources(sourceIds);
    };

    const handleTechniqueChange = (event, newValue) => {
        const techniqueIds = newValue.map(technique => technique.id);
        setSelectedTechniques(techniqueIds);
    };

    // Get selected topic objects for display
    const getSelectedTopicObjects = () => {
        return topics.filter(topic => selectedTopics.includes(topic.id));
    };

    // Get selected source objects for display
    const getSelectedSourceObjects = () => {
        return sources.filter(source => selectedSources.includes(source.id));
    };

    // Get selected technique objects for display
    const getSelectedTechniqueObjects = () => {
        return techniques.filter(technique => selectedTechniques.includes(technique.id));
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                maxWidth: 1000,
                width: 1000,
                margin: 'auto',
                p: 6,
                boxShadow: 3,
                borderRadius: 2,
                backgroundColor: 'white',
            }}
        >
            <Box sx={{ width: '100%' }}>
                <TextField
                    label="Problem (LaTeX)"
                    fullWidth
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    margin="normal"
                    multiline
                    rows={8}
                    variant="outlined"
                />
            </Box>
            <Box sx={{ width: '100%', mt: 3 }}>
                <TextField
                    label="Solution (LaTeX)"
                    fullWidth
                    value={solution}
                    onChange={(e) => setSolution(e.target.value)}
                    margin="normal"
                    multiline
                    rows={6}
                    variant="outlined"
                />
            </Box>
            <Box sx={{ width: '100%', mt: 3 }}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel>Difficulty</InputLabel>
                    <Select
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value)}
                        label="Difficulty"
                    >
                        <MenuItem value="">
                            <em>Select difficulty</em>
                        </MenuItem>
                        {difficultyOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ width: '100%', mt: 3 }}>
                <Autocomplete
                    multiple
                    id="technique-autocomplete"
                    options={techniques}
                    getOptionLabel={(option) => option.name}
                    value={getSelectedTechniqueObjects()}
                    onChange={handleTechniqueChange}
                    filterSelectedOptions
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Techniques"
                            placeholder="Search and select techniques..."
                            variant="outlined"
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })}
                                key={option.id}
                            />
                        ))
                    }
                    limitTags={3}
                    sx={{ width: '100%' }}
                />
            </Box>
            <Box sx={{ width: '100%', mt: 3 }}>
                <Autocomplete
                    multiple
                    id="curriculum-topics-autocomplete"
                    options={topics}
                    getOptionLabel={(option) => option.name}
                    value={getSelectedTopicObjects()}
                    onChange={handleTopicChange}
                    filterSelectedOptions
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Curriculum Topics"
                            placeholder="Search and select topics..."
                            variant="outlined"
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })}
                                key={option.id}
                            />
                        ))
                    }
                    limitTags={3}
                    sx={{ width: '100%' }}
                />
            </Box>
            <Box sx={{ width: '100%', mt: 3 }}>
                <Autocomplete
                    multiple
                    id="sources-autocomplete"
                    options={sources}
                    getOptionLabel={(option) => option.name}
                    value={getSelectedSourceObjects()}
                    onChange={handleSourceChange}
                    filterSelectedOptions
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Sources"
                            placeholder="Search and select sources..."
                            variant="outlined"
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })}
                                key={option.id}
                            />
                        ))
                    }
                    limitTags={3}
                    sx={{ width: '100%' }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button type="submit" variant="contained" color="primary" sx={{ flex: 1 }}>
                    Save
                </Button>
                <Button
                    type="button"
                    variant="outlined"
                    color="secondary"
                    sx={{ flex: 1 }}
                >
                    Preview
                </Button>
            </Box>

            {(content || solution) && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h5" gutterBottom>
                        Preview
                    </Typography>

                    {content && (
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Problem Statement:
                            </Typography>
                            <MathRenderer formula={content} />
                        </Paper>
                    )}

                    {solution && (
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Solution:
                            </Typography>
                            <MathRenderer formula={solution} />
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default ProblemForm;